import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Plus, History, Zap, Mail } from "lucide-react";
import { useDemoMode, BUILDER } from "@/lib/demo";
import { Tour } from "@/components/Tour";

const NAV = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, tour: "nav-dashboard", end: true },
  { to: "/new", label: "New Test", icon: Plus, tour: "nav-new" },
  { to: "/history", label: "History", icon: History, tour: "nav-history" },
];

export const Layout = () => {
  const [demo, setDemo] = useDemoMode();
  const loc = useLocation();
  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/[0.06] bg-[#0C0D14]/80 backdrop-blur-xl sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3 px-6 py-6" data-testid="logo-link" aria-label="GAUNTLET home">
          <span className="grid h-9 w-9 place-items-center rounded-xl grad-bg text-ink shadow-[0_0_24px_rgba(34,211,238,0.35)]"><Zap size={18} strokeWidth={2.5} /></span>
          <span className="font-heading text-xl font-bold tracking-tight">GAUNTLET</span>
        </Link>
        <nav className="flex flex-col gap-1 px-3 mt-2" aria-label="Main">
          {NAV.map(({ to, label, icon: Icon, tour, end }) => (
            <NavLink key={to} to={to} end={end} data-tour={tour} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-white/[0.07] text-white" : "text-white/55 hover:bg-white/[0.04] hover:text-white"}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto px-6 pb-6 text-[11px] leading-relaxed text-white/35">
          <p>Agent · Customer · Judge — three models, one verdict.</p>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-4 border-b border-white/[0.06] bg-ink/70 px-5 py-3 backdrop-blur-xl md:px-10">
          <div className="flex items-center gap-3 md:hidden">
            <Link to="/" className="font-heading font-bold" data-testid="logo-link-mobile">GAUNTLET</Link>
            <nav className="flex gap-3" aria-label="Mobile">{NAV.map((n) => { const Icon = n.icon; return <NavLink key={n.to} to={n.to} end={n.end} aria-label={n.label} className={({ isActive }) => `text-xs ${isActive ? "text-white" : "text-white/50"}`}><Icon size={16} /></NavLink>; })}</nav>
          </div>
          <p className="hidden md:block text-xs text-white/40 font-mono">{loc.pathname}</p>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-tour="demo-toggle">
            <span className="hidden sm:inline text-xs font-medium text-white/70 whitespace-nowrap">Demo mode</span>
            <button role="switch" aria-checked={demo} aria-label="Toggle demo mode" data-testid="demo-mode-toggle" onClick={() => setDemo(!demo)}
              className={`relative h-6 w-11 rounded-full transition-colors duration-200 ${demo ? "grad-bg" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${demo ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
            {demo && <span className="chip hidden md:inline-flex text-[10px] text-cyan-300 border-cyan-400/30" data-testid="demo-mode-chip">REPLAY · NO LLM</span>}
          </label>
        </header>
        <main className="flex-1 px-5 py-8 md:px-10 md:py-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
        <footer className="border-t border-white/[0.06] px-5 py-5 text-xs text-white/40 md:px-10 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between" data-testid="footer">
          <span>All customers are AI-simulated. Sample compliance rules are illustrative, not legal advice.</span>
          <a href={`mailto:${BUILDER.email}?subject=GAUNTLET`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="footer-builder-link">
            Built by {BUILDER.name}. Let's talk <Mail size={12} />
          </a>
        </footer>
      </div>
      <Tour />
    </div>
  );
};
